import React, { useEffect, useMemo, useState } from 'react';
import Btn from '../../../components/ui/Btn';
import Field, { TextInput, TextArea } from '../../../components/ui/Field';
import Switch from '../../../components/Switch';
import AgentDot from '../../../components/ui/AgentDot';
import { useTheme } from '../../../context/ThemeContext';
import { AVATARS, LIMITS, PALETTES, POSITIONS, THEMES, onAccent, resolveLook } from '../../../utils/agentLook';

const HEX = /^#[0-9a-fA-F]{6}$/;

/** De la apariencia guardada al estado del formulario. */
function toForm(look) {
  const s = look || {};
  return {
    title: s.title || '', greeting: s.greeting || '', teaser: s.teaser || '', placeholder: s.placeholder || '', disclaimer: s.disclaimer || '',
    palette: s.colors ? 'custom' : s.palette || null,   // null = la que sale del nombre
    colors: s.colors || { a: '#7C5CFF', b: '#2BD9FE' },
    theme: s.theme || 'auto', avatar: s.avatar || 'dot', avatarUrl: s.avatarUrl || '', position: s.position || 'right', follow: s.follow !== false,
  };
}
/** Del formulario a lo que se manda al motor: solo lo que cambia el valor por defecto. */
function toStored(f) {
  const out = {};
  for (const k of Object.keys(LIMITS)) if (f[k].trim()) out[k] = f[k].trim();
  if (f.palette === 'custom') out.colors = { a: f.colors.a.toUpperCase(), b: f.colors.b.toUpperCase() };
  else if (f.palette) out.palette = f.palette;
  if (f.theme !== 'auto') out.theme = f.theme;
  if (f.avatar !== 'dot') out.avatar = f.avatar;
  if (f.avatarUrl.trim()) out.avatarUrl = f.avatarUrl.trim();
  if (f.position !== 'right') out.position = f.position;
  if (!f.follow) out.follow = false;
  return out;
}

const seg = (active) => `h-9 px-4 rounded-full text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70 ${active ? 'bg-panel text-fg shadow-sm ring-1 ring-line' : 'text-fg-dim hover:text-fg'}`;
function Segmented({ label, value, options, onChange }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-full bg-raised p-1 ring-1 ring-inset ring-line">
      {options.map((o) => <button key={o.key} type="button" role="radio" aria-checked={value === o.key} onClick={() => onChange(o.key)} className={seg(value === o.key)}>{o.label}</button>)}
    </div>
  );
}
const Group = ({ title, hint, children }) => (
  <fieldset className="space-y-3 min-w-0">
    <legend className="font-display text-[16px] font-bold text-fg">{title}</legend>
    {hint && <p className="text-[12.5px] text-fg-faint -mt-1">{hint}</p>}
    {children}
  </fieldset>
);

/** Vista previa fiel del chat: mismos colores, tema, avatar y textos que verá el visitante. */
export function WebChatPreview({ look, agentName }) {
  const { resolved } = useTheme();
  const dark = look.theme === 'auto' ? resolved === 'dark' : look.theme === 'dark';
  const v = dark
    ? { bg: '#17151f', fg: '#f4f2fb', mute: '#a19db4', line: '#2a2736', soft: '#211e2c' }
    : { bg: '#ffffff', fg: '#14121f', mute: '#6b6880', line: '#ece9f5', soft: '#f6f4fb' };
  const grad = `linear-gradient(135deg, ${look.colors.a}, ${look.colors.b})`;
  const dotProps = { colors: look.colors, shape: look.avatar, imageUrl: look.avatarUrl, follow: look.follow };
  return (
    <div className={`flex flex-col gap-3 ${look.position === 'left' ? 'items-start' : 'items-end'}`} aria-label="Vista previa del chat">
      <div className="w-full max-w-[300px] rounded-[22px] overflow-hidden" style={{ background: v.bg, color: v.fg, border: `1px solid ${v.line}`, boxShadow: '0 18px 44px rgb(20 10 50 / .25)' }}>
        <div className="flex items-center gap-2.5 px-3.5 pt-3.5 pb-2.5">
          <AgentDot name={agentName} size={32} {...dotProps} />
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-bold truncate">{look.title}</p>
            <p className="text-[11.5px] flex items-center gap-1.5" style={{ color: v.mute }}><span className="h-1.5 w-1.5 rounded-full bg-[#22c55e]" />En línea</p>
          </div>
        </div>
        <div className="px-3.5 pb-3 flex flex-col gap-2.5 min-h-[190px]">
          <div className="flex flex-col items-center text-center gap-2 pt-3">
            <AgentDot name={agentName} size={54} state="happy" {...dotProps} />
            <p className="text-[13px] leading-snug max-w-[26ch] whitespace-pre-wrap break-words">{look.greeting}</p>
          </div>
          <p className="self-end text-[12.5px] px-3 py-2 rounded-[16px_16px_4px_16px]" style={{ background: grad, color: look.onAccent }}>¿Tienen citas mañana?</p>
          <p className="text-[12.5px]">Claro, tenemos a las <b>10:00</b> y a las <b>16:30</b>.</p>
        </div>
        <div className="mx-2.5 mb-2 flex items-center gap-2 rounded-[18px] pl-3.5 pr-1.5 py-1.5" style={{ background: v.soft, border: `1px solid ${v.line}` }}>
          <span className="flex-1 text-[12.5px] truncate" style={{ color: v.mute }}>{look.placeholder}</span>
          <span className="h-7 w-7 rounded-full grid place-items-center text-[13px] font-bold" style={{ background: grad, color: look.onAccent }} aria-hidden>↑</span>
        </div>
        <p className="text-[10.5px] text-center px-3 pb-2.5" style={{ color: v.mute }}>{look.disclaimer}</p>
      </div>
      <div className={`flex items-end gap-2 ${look.position === 'left' ? 'flex-row' : 'flex-row-reverse'}`}>
        <AgentDot name={agentName} size={46} {...dotProps} />
        <p className="max-w-[170px] rounded-2xl px-3 py-2 text-[12px] leading-snug break-words" style={{ background: v.bg, color: v.fg, border: `1px solid ${v.line}`, boxShadow: '0 8px 20px rgb(20 10 50 / .15)' }}>{look.teaser}</p>
      </div>
    </div>
  );
}

/** Editor de la apariencia del chat web. Se guarda en la conexión y se aplica al momento (no hay que publicar). */
export default function WebChatLook({ agentName, stored, readOnly, onSave }) {
  const [f, setF] = useState(() => toForm(stored));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const storedKey = JSON.stringify(stored || {});
  useEffect(() => { setF(toForm(stored)); }, [storedKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const set = (patch) => { setF((x) => ({ ...x, ...patch })); setSaved(false); };

  const payload = useMemo(() => toStored(f), [f]);
  const dirty = JSON.stringify(payload) !== JSON.stringify(stored || {});
  const colorsOk = f.palette !== 'custom' || (HEX.test(f.colors.a) && HEX.test(f.colors.b));
  // Lo que se ve: el formulario resuelto con los mismos valores por defecto que el motor
  const look = useMemo(() => resolveLook(agentName, null, colorsOk ? payload : { ...payload, colors: undefined }), [agentName, payload, colorsOk]);

  const save = async (value) => {
    setBusy(true); setError('');
    try { await onSave(value); setSaved(true); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const palSwatch = (key, p) => (
    <label key={key} className={`relative cursor-pointer rounded-full p-0.5 ring-2 transition ${f.palette === key || (!f.palette && look.palette === key) ? 'ring-live' : 'ring-transparent hover:ring-fg/20'} focus-within:ring-live/70`}>
      <input type="radio" name="palette" value={key} checked={f.palette === key || (!f.palette && look.palette === key)} onChange={() => set({ palette: key })} className="sr-only" disabled={readOnly} />
      <span className="block h-8 w-8 rounded-full" style={{ background: `radial-gradient(120% 120% at 30% 20%, ${p.b} 0%, ${p.a} 62%)` }} title={p.name} />
      <span className="sr-only">{p.name}</span>
    </label>
  );
  const count = (k) => <span className="text-fg-faint tabular-nums">{f[k].length}/{LIMITS[k]}</span>;

  return (
    <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_330px]">
      <div className="space-y-8 min-w-0">
        <Group title="Textos" hint="Si dejas un campo vacío se usa el de siempre.">
          <Field label="Nombre en el chat" hint={<>Por defecto: «{agentName}». {count('title')}</>}>
            <TextInput value={f.title} onChange={(e) => set({ title: e.target.value })} maxLength={LIMITS.title} placeholder={agentName} disabled={readOnly} />
          </Field>
          <Field label="Saludo" hint={<>Lo primero que ve la persona al abrir el chat. {count('greeting')}</>}>
            <TextArea rows={2} value={f.greeting} onChange={(e) => set({ greeting: e.target.value })} maxLength={LIMITS.greeting} placeholder={`¡Hola! Soy ${agentName}. ¿En qué te ayudo?`} disabled={readOnly} />
          </Field>
          <Field label="Aviso junto al botón del chat" hint={<>Aparece unos segundos después de cargar la página, antes de abrir el chat. Por defecto usa el saludo. {count('teaser')}</>}>
            <TextInput value={f.teaser} onChange={(e) => set({ teaser: e.target.value })} maxLength={LIMITS.teaser} placeholder="¿Te ayudo con algo?" disabled={readOnly} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Texto del cuadro de mensaje" hint={count('placeholder')}>
              <TextInput value={f.placeholder} onChange={(e) => set({ placeholder: e.target.value })} maxLength={LIMITS.placeholder} placeholder="Escribe tu mensaje…" disabled={readOnly} />
            </Field>
            <Field label="Nota al pie" hint={count('disclaimer')}>
              <TextInput value={f.disclaimer} onChange={(e) => set({ disclaimer: e.target.value })} maxLength={LIMITS.disclaimer} placeholder="Respuestas generadas con IA…" disabled={readOnly} />
            </Field>
          </div>
        </Group>

        <Group title="Colores" hint="Es el color de las burbujas, el botón de enviar y el avatar.">
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(PALETTES).map(([key, p]) => palSwatch(key, p))}
            <label className={`relative cursor-pointer rounded-full p-0.5 ring-2 transition ${f.palette === 'custom' ? 'ring-live' : 'ring-transparent hover:ring-fg/20'} focus-within:ring-live/70`}>
              <input type="radio" name="palette" value="custom" checked={f.palette === 'custom'} onChange={() => set({ palette: 'custom' })} className="sr-only" disabled={readOnly} />
              <span className="grid h-8 min-w-[88px] place-items-center rounded-full px-3 text-[12px] font-semibold text-fg bg-raised ring-1 ring-inset ring-line">Propios</span>
            </label>
          </div>
          {f.palette === 'custom' && (
            <div className="flex flex-wrap items-end gap-4 rounded-2xl bg-raised/50 p-4 ring-1 ring-line">
              {[['a', 'Color principal'], ['b', 'Color secundario']].map(([k, label]) => (
                <div key={k} className="flex flex-col gap-1.5">
                  <label htmlFor={`color-${k}`} className="text-[13px] font-medium text-fg">{label}</label>
                  <div className="flex items-center gap-2">
                    <input id={`color-${k}`} type="color" value={HEX.test(f.colors[k]) ? f.colors[k] : '#000000'} onChange={(e) => set({ colors: { ...f.colors, [k]: e.target.value } })} disabled={readOnly}
                      className="h-10 w-12 cursor-pointer rounded-lg border border-line bg-transparent p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70" />
                    <TextInput aria-label={`${label} en hexadecimal`} value={f.colors[k]} onChange={(e) => set({ colors: { ...f.colors, [k]: e.target.value } })} maxLength={7} spellCheck={false} className="w-28 font-mono uppercase" disabled={readOnly} />
                  </div>
                </div>
              ))}
              {!colorsOk && <p role="alert" className="basis-full text-[12.5px] text-signal">Escribe cada color como #7C5CFF (con # y seis caracteres).</p>}
              {colorsOk && <p className="basis-full text-[12.5px] text-fg-faint">El texto sobre los colores se ajusta solo para que se lea ({onAccent(f.colors.a, f.colors.b) === '#FFFFFF' ? 'claro' : 'oscuro'}).</p>}
            </div>
          )}
        </Group>

        <Group title="Tema" hint="«Automático» sigue el modo claro u oscuro de la persona que visita el sitio.">
          <Segmented label="Tema del chat" value={f.theme} options={THEMES} onChange={(theme) => set({ theme })} />
        </Group>

        <Group title="Avatar">
          <div role="radiogroup" aria-label="Forma del avatar" className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            {[...AVATARS, { key: 'image', label: 'Imagen' }].map((o) => {
              const active = o.key === 'image' ? Boolean(f.avatarUrl.trim()) : !f.avatarUrl.trim() && f.avatar === o.key;
              return (
                <button key={o.key} type="button" role="radio" aria-checked={active} disabled={readOnly}
                  onClick={() => (o.key === 'image' ? set({ avatarUrl: f.avatarUrl || 'https://' }) : set({ avatar: o.key, avatarUrl: '' }))}
                  className={`flex flex-col items-center gap-2 rounded-2xl p-3 text-[13px] font-semibold ring-1 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70
                    ${active ? 'bg-raised ring-live text-fg' : 'bg-transparent ring-line text-fg-dim hover:text-fg hover:ring-fg/20'}`}>
                  {o.key === 'image'
                    ? <span className="grid h-12 w-12 place-items-center rounded-full bg-raised text-fg-faint text-[11px] ring-1 ring-line" aria-hidden>logo</span>
                    : <AgentDot name={agentName} size={44} colors={look.colors} shape={o.key} follow={f.follow} />}
                  {o.label}
                </button>
              );
            })}
          </div>
          {f.avatarUrl.trim() !== '' && (
            <Field label="Dirección de la imagen" hint="Una imagen cuadrada en https (se recorta en círculo). Si no carga, se usa el avatar normal.">
              <TextInput type="url" value={f.avatarUrl} onChange={(e) => set({ avatarUrl: e.target.value })} placeholder="https://www.cliente.mx/logo.png" maxLength={300} spellCheck={false} disabled={readOnly} />
            </Field>
          )}
          <div className="flex items-center justify-between gap-4 rounded-2xl bg-raised/50 px-4 py-3 ring-1 ring-line">
            <div>
              <p className="text-[14px] font-semibold text-fg" id="follow-label">Los ojos siguen el cursor</p>
              <p className="text-[12.5px] text-fg-faint">Miran hacia donde mueves el mouse y hacia el cuadro de texto mientras escribes. Con «reducir movimiento» activado en el equipo no se mueven.</p>
            </div>
            <Switch checked={f.follow} onChange={(follow) => set({ follow })} label="Los ojos siguen el cursor" disabled={readOnly} />
          </div>
        </Group>

        <Group title="Posición" hint="En qué esquina de la pantalla aparece el botón del chat.">
          <Segmented label="Posición del chat" value={f.position} options={POSITIONS} onChange={(position) => set({ position })} />
        </Group>

        <div className="flex flex-wrap items-center gap-3 border-t border-line pt-5">
          <Btn variant="primary" onClick={() => save(payload)} disabled={!dirty || readOnly || !colorsOk} loading={busy}>Guardar y aplicar</Btn>
          <Btn variant="ghost" onClick={() => { setF(toForm({})); setSaved(false); }} disabled={readOnly || busy || JSON.stringify(payload) === '{}'}>Restablecer valores</Btn>
          <span role="status" aria-live="polite" className="text-[12.5px] text-fg-faint">
            {error ? <span className="text-signal">{error}</span> : saved && !dirty ? <span className="text-live">Aplicado: ya se ve en el chat.</span> : dirty ? 'Cambios sin guardar. Se aplican al momento, sin publicar.' : 'Sin cambios.'}
          </span>
        </div>
      </div>

      <aside className="xl:sticky xl:top-4 self-start rounded-3xl bg-raised/40 ring-1 ring-line p-4 sm:p-5" aria-label="Vista previa">
        <p className="text-[13px] font-semibold text-fg mb-3">Vista previa</p>
        <WebChatPreview look={look} agentName={agentName} />
      </aside>
    </div>
  );
}
